const storageKey = 'ntfl-color-mode';
const root = document.documentElement;

function getPreferredTheme() {
  const storedTheme = window.localStorage.getItem(storageKey);
  if (storedTheme === 'light' || storedTheme === 'dark') return storedTheme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme) {
  root.dataset.ntflTheme = theme;
  document.querySelectorAll('[data-ntfl-theme-toggle]').forEach((button) => {
    const isDark = theme === 'dark';
    button.setAttribute('aria-pressed', String(isDark));
    button.setAttribute('aria-label', isDark ? 'Use light theme' : 'Use dark theme');
    const label = button.querySelector('[data-ntfl-theme-label]');
    if (label) label.textContent = isDark ? 'Light mode' : 'Dark mode';
  });
}

applyTheme(getPreferredTheme());

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-ntfl-theme-toggle]');
  if (!toggle) return;

  const nextTheme = root.dataset.ntflTheme === 'dark' ? 'light' : 'dark';
  window.localStorage.setItem(storageKey, nextTheme);
  applyTheme(nextTheme);
});

window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
  if (window.localStorage.getItem(storageKey)) return;
  applyTheme(event.matches ? 'dark' : 'light');
});

const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
let revealObserver;
let parallaxStarted = false;
let parallaxFrame;

function getRevealObserver() {
  if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12,
    },
  );

  return revealObserver;
}

function observeMotionElement(element) {
  if (motionPreference.matches) {
    element.classList.add('is-visible');
    return;
  }

  const observer = getRevealObserver();
  if (observer) {
    observer.observe(element);
  } else {
    element.classList.add('is-visible');
  }
}

function addReveal(element, type = 'up', delay = 0) {
  if (element.dataset.ntflMotionReady === 'true') return;
  element.dataset.ntflMotionReady = 'true';
  element.dataset.ntflReveal = type;
  element.classList.add('ntfl-reveal');
  element.style.setProperty('--ntfl-reveal-delay', `${delay}ms`);
  observeMotionElement(element);
}

function addRevealGroup(scope, containerSelector, itemSelector, type = 'up', stagger = 70) {
  scope.querySelectorAll(containerSelector).forEach((container) => {
    container.querySelectorAll(itemSelector).forEach((item, index) => {
      addReveal(item, type, Math.min(index * stagger, 490));
    });
  });
}

function splitHeading(heading) {
  if (heading.dataset.ntflWordReveal === 'true') return;

  const words = heading.textContent.trim().split(/\s+/);
  if (!words.length) return;

  heading.textContent = '';
  heading.dataset.ntflWordReveal = 'true';
  heading.classList.add('ntfl-word-reveal');

  words.forEach((word, index) => {
    const mask = document.createElement('span');
    const inner = document.createElement('span');
    mask.className = 'ntfl-word-reveal__mask';
    inner.className = 'ntfl-word-reveal__word';
    inner.textContent = word;
    inner.style.setProperty('--ntfl-word-index', index);
    mask.append(inner);
    heading.append(mask);
    if (index < words.length - 1) heading.append(document.createTextNode(' '));
  });

  observeMotionElement(heading);
}

function prepareParallax(scope) {
  scope
    .querySelectorAll(
      '.ntfl-drop-hero__visual, .ntfl-product-index .ntfl-index-card__media, .ntfl-categories .ntfl-category-card__visual',
    )
    .forEach((element) => {
      element.dataset.ntflGridParallax = 'true';
    });

  scope
    .querySelectorAll(
      '.ntfl-drop-hero__frame--primary .ntfl-drop-hero__image, .ntfl-collection-feature__visual .ntfl-collection-feature__image',
    )
    .forEach((element) => {
      element.dataset.ntflImageParallax = 'true';
    });
}

function updateParallax() {
  parallaxFrame = undefined;
  if (motionPreference.matches) return;

  const viewportHeight = window.innerHeight;

  document.querySelectorAll('[data-ntfl-grid-parallax]').forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > viewportHeight) return;
    const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height) - 0.5;
    element.style.setProperty('--ntfl-grid-offset', `${progress * 18}px`);
  });

  document.querySelectorAll('[data-ntfl-image-parallax]').forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > viewportHeight) return;
    const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height) - 0.5;
    const scale = 1.018 + Math.abs(progress) * 0.025;
    const offset = progress * -12;
    element.style.setProperty('--ntfl-scroll-scale', scale.toFixed(4));
    element.style.setProperty('--ntfl-image-offset', `${offset}px`);
  });
}

function requestParallaxUpdate() {
  if (parallaxFrame) return;
  parallaxFrame = window.requestAnimationFrame(updateParallax);
}

function startParallax() {
  if (parallaxStarted || motionPreference.matches) return;
  parallaxStarted = true;
  window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
  window.addEventListener('resize', requestParallaxUpdate);
  requestParallaxUpdate();
}

function initializeMotion(scope = document) {
  if (motionPreference.matches) return;

  root.classList.add('ntfl-motion-ready');

  scope
    .querySelectorAll(
      '.ntfl-drop-hero__title, .ntfl-product-index__title, .ntfl-categories__header h2, .ntfl-collection-feature__title',
    )
    .forEach(splitHeading);

  [
    ['.ntfl-drop-hero__release', 'fade'],
    ['.ntfl-drop-hero__copy', 'up'],
    ['.ntfl-drop-hero__facts', 'up'],
    ['.ntfl-drop-hero__actions', 'up'],
    ['.ntfl-drop-hero__frame--primary', 'scale'],
    ['.ntfl-drop-hero__frame--secondary', 'up'],
    ['.ntfl-drop-hero__frame--detail', 'scale'],
    ['.ntfl-product-index__heading', 'up'],
    ['.ntfl-product-index__intro', 'up'],
    ['.ntfl-categories__header > div:first-child', 'up'],
    ['.ntfl-categories__header-aside', 'up'],
    ['.ntfl-collection-feature__visual', 'scale'],
    ['.product-information__media', 'fade'],
    ['.product-details > .group-block', 'up'],
    ['.ntfl-footer__signup-copy', 'up'],
    ['.ntfl-footer__form', 'up'],
  ].forEach(([selector, type], index) => {
    scope.querySelectorAll(selector).forEach((element) => addReveal(element, type, (index % 3) * 55));
  });

  addRevealGroup(scope, '.ntfl-product-index__grid', '.ntfl-index-card, .ntfl-index-set', 'up', 65);
  addRevealGroup(scope, '.ntfl-categories__grid', '.ntfl-category-card', 'up', 80);
  addRevealGroup(scope, '.ntfl-collection-feature__content', ':scope > *', 'up', 70);
  addRevealGroup(scope, '.ntfl-footer__directory', ':scope > *', 'up', 60);
  addRevealGroup(scope, '.product-recommendations', '.product-card', 'up', 55);

  prepareParallax(scope);
  startParallax();
}

function disableMotion() {
  root.classList.remove('ntfl-motion-ready');
  document.querySelectorAll('.ntfl-reveal, .ntfl-word-reveal').forEach((element) => {
    element.classList.add('is-visible');
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeMotion());
} else {
  initializeMotion();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeMotion(event.target);
  requestParallaxUpdate();
});

motionPreference.addEventListener('change', (event) => {
  if (event.matches) {
    disableMotion();
  } else {
    initializeMotion();
    requestParallaxUpdate();
  }
});
